'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { useMutation, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-react'
import { MixerHorizontalIcon } from '@radix-ui/react-icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DELETE_MANY_CATEGORIES, GET_CATEGORIES_TREE } from './categoriesQueries'
import CategoryForm from './components/CategoryForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { PageContent } from '@/components/shared/page-content'
import { useLanguage } from '@/context/LanguageContext'
import { useConfirmation } from '@/context/ConfirmationContext'
import { Skeleton } from '@/components/ui/skeleton'
import { CategoryTreeNode } from './types'
import { toast } from 'sonner'

export default function Categories() {
  const { language: currentLanguage } = useLanguage()
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set())
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set())
  const [isMultiSelect, setIsMultiSelect] = useState(false)
  const { showConfirmation } = useConfirmation()

  const { data, loading } = useQuery(GET_CATEGORIES_TREE, {
    fetchPolicy: 'cache-and-network',
  })

  const [deleteManyCategories] = useMutation(DELETE_MANY_CATEGORIES, {
    refetchQueries: ['GET_CATEGORIES_TREE'],
  })

  const categories: CategoryTreeNode[] = data?.getCategoriesAndChildsQuery || []

  // Expand all root categories by default on first load
  useEffect(() => {
    if (categories.length > 0 && expandedCategories.size === 0) {
      const roots = categories.filter(c => c.depth_level === 0)
      setExpandedCategories(new Set(roots.map(c => c.id)))
    }
  }, [categories])

  const openCategoryForm = useOpenDrawer(CategoryForm)

  const toggleExpand = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const getName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(p => p.language.toLowerCase() === currentLanguage.toLowerCase())?.name ||
      profiles[0]?.name ||
      ''
    )
  }

  const hasChildren = (category: CategoryTreeNode) => {
    return category.child_categories && category.child_categories.length > 0
  }

  // Visibility logic: a row is visible if ALL its ancestors are expanded
  const getVisibleCategories = (): CategoryTreeNode[] => {
    const visible: CategoryTreeNode[] = []
    let collapseAtDepth: number | null = null

    for (const cat of categories) {
      if (collapseAtDepth !== null) {
        if (cat.depth_level <= collapseAtDepth) {
          collapseAtDepth = null
        } else {
          continue
        }
      }

      visible.push(cat)

      if (hasChildren(cat) && !expandedCategories.has(cat.id)) {
        collapseAtDepth = cat.depth_level
      }
    }

    return visible
  }

  const visibleCategories = loading ? [] : getVisibleCategories()

  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedCategories.size === visibleCategories.length) {
      setSelectedCategories(new Set())
    } else {
      setSelectedCategories(new Set(visibleCategories.map(c => c.id)))
    }
  }

  const toggleMultiSelect = (value: boolean) => {
    setIsMultiSelect(value)
    if (!value) {
      setSelectedCategories(new Set())
    }
  }

  const handleBulkDelete = () => {
    const count = selectedCategories.size
    showConfirmation({
      onConfirm: async () => {
        try {
          await deleteManyCategories({
            variables: { ids: Array.from(selectedCategories) },
          })
          toast.success(`${count} category(ies) deleted successfully`)
          setSelectedCategories(new Set())
        } catch {
          toast.error('Failed to delete categories')
        }
      },
      description: `Are you sure you want to delete ${count} category(ies)? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Categories" />
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto hidden h-8 lg:flex"
              >
                <MixerHorizontalIcon className="mr-2 h-4 w-4" />
                View
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px]">
              <DropdownMenuLabel>View options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                className="capitalize"
                checked={isMultiSelect}
                onCheckedChange={(value) => toggleMultiSelect(!!value)}
              >
                Multi Select
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openCategoryForm}>Create New +</Button>
        </div>
      </div>

      {isMultiSelect && selectedCategories.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 rounded-md border">
          <span className="text-sm text-muted-foreground">
            {selectedCategories.size} selected
          </span>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </div>
      )}

      <div className="rounded-md border">
        {/* Table header */}
        <div className={`grid ${isMultiSelect ? 'grid-cols-[40px_1fr_120px_80px_100px]' : 'grid-cols-[1fr_120px_80px_100px]'} gap-2 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground border-b`}>
          {isMultiSelect && (
            <div className="flex items-center justify-center">
              <Checkbox
                checked={visibleCategories.length > 0 && selectedCategories.size === visibleCategories.length}
                onCheckedChange={toggleSelectAll}
              />
            </div>
          )}
          <div>Name</div>
          <div>Code</div>
          <div className="text-center">Order</div>
          <div className="text-right">Products</div>
        </div>

        {/* Table body */}
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : visibleCategories.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No categories yet. Create a category to get started.
          </div>
        ) : (
          <div>
            {visibleCategories.map(category => {
              const isExpandable = hasChildren(category)
              const isExpanded = expandedCategories.has(category.id)
              const indent = category.depth_level * 24

              return (
                <div
                  key={`cat-${category.id}`}
                  className={`grid ${isMultiSelect ? 'grid-cols-[40px_1fr_120px_80px_100px]' : 'grid-cols-[1fr_120px_80px_100px]'} gap-2 px-4 py-2.5 border-b hover:bg-muted/30 cursor-pointer items-center ${
                    selectedCategories.has(category.id) ? 'bg-muted/40' : ''
                  }`}
                  style={{
                    backgroundColor: selectedCategories.has(category.id)
                      ? undefined
                      : category.depth_level > 0
                        ? `rgba(0,0,0,${0.01 * category.depth_level})`
                        : undefined,
                  }}
                  onClick={() => openCategoryForm({ id: category.id })}
                >
                  {/* Checkbox */}
                  {isMultiSelect && (
                    <div className="flex items-center justify-center" onClick={e => toggleSelect(category.id, e)}>
                      <Checkbox checked={selectedCategories.has(category.id)} />
                    </div>
                  )}

                  {/* Name with expand/collapse toggle and indentation */}
                  <div
                    className={`text-sm flex items-center gap-1 ${category.depth_level === 0 ? 'font-medium' : ''}`}
                    style={{ paddingLeft: indent }}
                  >
                    {isExpandable ? (
                      <button
                        onClick={e => toggleExpand(category.id, e)}
                        className="flex items-center justify-center h-6 w-6 rounded hover:bg-muted shrink-0"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      <div className="w-6 shrink-0" />
                    )}
                    <span className="truncate">{getName(category.category_profiles)}</span>
                  </div>

                  {/* Code */}
                  <div className="text-xs text-muted-foreground truncate">
                    {category.code || '—'}
                  </div>

                  {/* Order */}
                  <div className="flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">
                      {category.menu_order != null ? category.menu_order : '—'}
                    </span>
                  </div>

                  {/* Products count */}
                  <div className="text-right">
                    <Badge variant="secondary" className="text-xs">
                      {category.product_containers.length}
                    </Badge>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </PageContent>
  )
}
